/**
 * SYNC RULES — usePrograms
 * ─────────────────────────────────────────────────
 * 1. All data fetching via useQuery with queryKey ["programs", userId]
 * 2. All mutations via useMutation with onSuccess → invalidateQueries(["programs"])
 * 3. setAsCurrentProgram MUST also invalidate ["current-program"] + emitProfileChange()
 * 4. The public API surface (returned object keys) MUST NOT change — consumer components depend on it
 * 5. Pagination uses a separate queryKey ["programs-page", userId, page, pageSize]
 */

import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { toastError } from "@/lib/errorToast";
import { emitProfileChange } from "@/lib/profileChangeEmitter";

export interface ProgramRecord {
  id: string;
  title: string;
  mode: string;
  detail_level: string;
  duration_weeks: number;
  duration_type?: string;
  target_date?: string | null;
  status: string;
  is_favorite: boolean;
  source_fact_ids: string[];
  content_json: any;
  meta: any;
  created_at: string;
  updated_at: string;
  active_week_types?: string[] | null;
  // Versioning
  version: number;
  parent_program_id?: string | null;
  version_reason?: string | null;
  versioned_at?: string | null;
  is_latest: boolean;
  // Coach provenance
  created_by_coach_id?: string | null;
  athlete_entity_id?: string | null;
}

/** Shared query key factory */
const programKeys = {
  all: (userId: string | null) => ["programs", userId] as const,
  page: (userId: string | null, page: number, pageSize: number) =>
    ["programs-page", userId, page, pageSize] as const,
  currentId: (userId: string | null) => ["programs-current-id", userId] as const,
};

export function usePrograms() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ── Main programs query ──
  const programsQuery = useQuery({
    queryKey: programKeys.all(user?.id ?? null),
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("programs")
        .select("*")
        .eq("user_id", user!.id)
        .eq("is_latest", true)
        .not("status", "in", "(generating,error)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as ProgramRecord[];
    },
  });

  const programs = programsQuery.data ?? [];
  const loading = programsQuery.isLoading;
  const programsCount = programs.length;

  // ── Current program ID query ──
  const currentProgramQuery = useQuery({
    queryKey: programKeys.currentId(user?.id ?? null),
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("user_intent_profiles")
        .select("current_program_id")
        .eq("user_id", user!.id)
        .maybeSingle();
      return (data as any)?.current_program_id || null;
    },
  });

  const currentProgramId = currentProgramQuery.data ?? null;

  /** Invalidate all program-related queries */
  const invalidatePrograms = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["programs"] });
    queryClient.invalidateQueries({ queryKey: ["programs-page"] });
  }, [queryClient]);

  // ── Legacy compat: fetchPrograms triggers invalidation ──
  const fetchPrograms = useCallback(async () => {
    invalidatePrograms();
  }, [invalidatePrograms]);

  // ── Paginated fetch (returns void — data flows via query) ──
  const fetchProgramsPage = useCallback(async (page: number, pageSize: number) => {
    if (!user) return;
    // For paginated views, we refetch the main query which is used everywhere
    invalidatePrograms();
  }, [user, invalidatePrograms]);

  // ── Helper: extract exercises from a block ──
  const getBlockExercises = (block: any) => {
    if (Array.isArray(block.exercises) && block.exercises.length > 0) {
      return block.exercises.map((ex: any) => ({
        name: ex.name || "Exercice",
        sets: ex.sets || undefined,
        reps: ex.reps || undefined,
        rest: ex.rest || undefined,
        notes: ex.notes || undefined,
        fact_ids: (ex.fact_ids || []).map((f: string) => f.replace(/^FACT-/i, "")),
      }));
    }
    const lines = (block.content || "").split("\n").filter((l: string) => l.trim().startsWith("-"));
    return lines.map((line: string) => {
      const clean = line.replace(/^-\s*/, "").replace(/\[FACT-[^\]]+\]/g, "").trim();
      const parts = clean.split(/[:(]/);
      return {
        name: parts[0]?.trim() || clean,
        notes: parts.slice(1).join(":").replace(/\)$/, "").trim() || undefined,
        fact_ids: (line.match(/FACT-([a-f0-9]+)/g) || []).map((f: string) => f.replace("FACT-", "")),
      };
    });
  };

  // ── Save program (complex, kept as async function) ──
  const saveProgram = async (
    program: Omit<ProgramRecord, "id" | "created_at" | "updated_at" | "is_favorite" | "version" | "is_latest" | "parent_program_id" | "version_reason" | "versioned_at">,
    linkedSessionIds?: string[],
    versioningContext?: {
      parent_program_id: string;
      version: number;
      version_reason: string;
    }
  ) => {
    if (!user) return null;

    const { data: { session: authSession } } = await supabase.auth.getSession();
    if (!authSession) {
      toastError("Session expired, please log in again", "Create a program");
      return null;
    }

    const profileFingerprint = sessionStorage.getItem("profile_fingerprint");
    const metaWithFingerprint = profileFingerprint
      ? { ...program.meta, profile_fingerprint: profileFingerprint }
      : program.meta;
    if (profileFingerprint) sessionStorage.removeItem("profile_fingerprint");

    const insertPayload: any = {
      ...program,
      meta: metaWithFingerprint,
      user_id: user.id,
    };

    if (versioningContext) {
      insertPayload.parent_program_id = versioningContext.parent_program_id;
      insertPayload.version = versioningContext.version;
      insertPayload.version_reason = versioningContext.version_reason;
      insertPayload.is_latest = false;
    }

    const { data, error } = await supabase
      .from("programs")
      .insert(insertPayload)
      .select()
      .single();
    if (error) {
      toastError("Save error", "Create a program", error.message);
      return null;
    }

    const programId = (data as any).id;

    const trainingBlocks = program.content_json?.training?.blocks || [];
    if (trainingBlocks.length > 0) {
      try {
        for (let i = 0; i < trainingBlocks.length; i++) {
          const block = trainingBlocks[i];
          const exercises = getBlockExercises(block);
          const factIds = (block.fact_ids || []).map((f: string) => f.replace(/^FACT-/i, ""));

          const { data: sessionData, error: sessionError } = await supabase
            .from("sessions")
            .insert({
              user_id: user.id,
              title: block.title || `Séance ${i + 1}`,
              objective: block.objective || block.conditions || "",
              exercises: exercises,
              duration_minutes: block.duration_minutes || 60,
              level_required: block.level_required || program.meta?.metadata?.user_level || "Intermédiaire",
              contraindications: [],
              warnings: block.alternatives ? [`Alternative : ${block.alternatives}`] : [],
              fact_ids: factIds,
              tags: block.tags || [],
              mode: "ai",
              status: "active",
            } as any)
            .select("id")
            .single();

          if (sessionData && !sessionError) {
            await supabase.from("program_sessions").insert({
              program_id: programId,
              session_id: (sessionData as any).id,
              sort_order: i,
            } as any);
          }
        }
      } catch (e) {
        console.error("Error creating sessions from blocks:", e);
      }
    }

    if (linkedSessionIds && linkedSessionIds.length > 0) {
      const lastSortOrder = trainingBlocks.length;
      try {
        for (let i = 0; i < linkedSessionIds.length; i++) {
          await supabase.from("program_sessions").insert({
            program_id: programId,
            session_id: linkedSessionIds[i],
            sort_order: lastSortOrder + i,
          } as any);
        }
      } catch (e) {
        console.error("Error linking existing sessions:", e);
      }
    }

    await supabase.from("notifications").insert({
      user_id: user.id,
      type: "program_saved",
      title: "📦 Programme enregistré",
      body: `"${(data as any).title}" a été ajouté à ta bibliothèque.`,
      meta: { program_id: programId, program_title: (data as any).title },
    } as any);
    queryClient.invalidateQueries({ queryKey: ["notifications"] });

    invalidatePrograms();
    queryClient.invalidateQueries({ queryKey: ["sessions"] });
    localStorage.setItem("perform_program_updated_at", new Date().toISOString());
    return data as unknown as ProgramRecord;
  };

  /** Activate a draft version */
  const activateProgramVersion = async (programId: string): Promise<boolean> => {
    const { error } = await supabase.rpc("activate_program_version", {
      _program_id: programId,
    });
    if (error) {
      toastError("Version activation error", "Activate version", error.message);
      return false;
    }
    toast.success("New version activated");
    invalidatePrograms();
    localStorage.setItem("perform_program_updated_at", new Date().toISOString());
    return true;
  };

  /** Check if a draft version already exists in a program's lineage */
  const checkLineageDraftExists = async (programId: string): Promise<{ exists: boolean; draftId?: string; draftTitle?: string }> => {
    if (!user) return { exists: false };
    const { data } = await supabase
      .from("programs")
      .select("id, title")
      .eq("user_id", user.id)
      .eq("parent_program_id", programId)
      .eq("status", "draft")
      .eq("is_latest", false)
      .limit(1)
      .maybeSingle();

    if (data) {
      return { exists: true, draftId: (data as any).id, draftTitle: (data as any).title };
    }
    return { exists: false };
  };

  // ── Mutations ──

  const updateProgramStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("programs")
      .update({ status } as any)
      .eq("id", id);
    if (error) toastError("Update error", "Update program status", error.message);
    else {
      invalidatePrograms();
      localStorage.setItem("perform_program_updated_at", new Date().toISOString());
    }
  };

  const duplicateProgram = async (id: string) => {
    const source = programs.find((p) => p.id === id);
    if (!source || !user) return;
    const { data, error } = await supabase.from("programs").insert({
      user_id: user.id,
      title: `${source.title} (copie)`,
      mode: source.mode,
      detail_level: source.detail_level,
      duration_weeks: source.duration_weeks,
      status: "draft",
      source_fact_ids: source.source_fact_ids,
      content_json: source.content_json,
      meta: source.meta,
    } as any).select().single();
    if (error) toastError("Duplication error", "Duplicate program", error.message);
    else {
      const newProgramId = (data as any).id;
      const programTitle = (data as any).title;

      try {
        const { data: sourceSessions } = await supabase
          .from("program_sessions")
          .select("session_id, sort_order, scheduled_time, note, day_of_week")
          .eq("program_id", id)
          .order("sort_order", { ascending: true });

        if (sourceSessions && sourceSessions.length > 0) {
          const cloned = sourceSessions.map((ps: any) => ({
            program_id: newProgramId,
            session_id: ps.session_id,
            sort_order: ps.sort_order,
            scheduled_time: ps.scheduled_time,
            note: ps.note,
            day_of_week: ps.day_of_week,
          }));
          await supabase.from("program_sessions").insert(cloned as any);
        }
      } catch (e) {
        console.error("Error cloning program_sessions:", e);
      }

      toast.success("Program duplicated");
      await supabase.from("notifications").insert({
        user_id: user.id,
        type: "program_saved",
        title: "📦 Programme enregistré",
        body: `"${programTitle}" a été ajouté à ta bibliothèque.`,
        meta: { program_id: newProgramId, program_title: programTitle },
      } as any);
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      invalidatePrograms();
      localStorage.setItem("perform_program_updated_at", new Date().toISOString());
    }
  };

  const setAsCurrentProgram = async (id: string | null) => {
    if (!user) return;
    const { error } = await supabase
      .from("user_intent_profiles")
      .update({ current_program_id: id } as any)
      .eq("user_id", user.id);
    if (error) toastError("Erreur", "Activation programme", error.message);
    else {
      toast.success(id ? "Programme activé" : "Programme désactivé");
      queryClient.invalidateQueries({ queryKey: programKeys.currentId(user.id) });
      queryClient.invalidateQueries({ queryKey: ["current-program"] });
      emitProfileChange();
      localStorage.setItem("perform_program_updated_at", new Date().toISOString());
    }
  };

  const updateProgramContent = async (id: string, contentJson: any) => {
    const { error } = await supabase
      .from("programs")
      .update({ content_json: contentJson } as any)
      .eq("id", id);
    if (error) toastError("Update error", "Update program content", error.message);
    else {
      invalidatePrograms();
      localStorage.setItem("perform_program_updated_at", new Date().toISOString());
    }
  };

  const toggleProgramFavorite = async (id: string, current: boolean) => {
    const { error } = await supabase
      .from("programs")
      .update({ is_favorite: !current } as any)
      .eq("id", id);
    if (error) toastError("Update error", "Update program favorite", error.message);
    else invalidatePrograms();
  };

  const setActiveWeekTypes = async (id: string, weekTypes: string[] | null) => {
    const payload = weekTypes && weekTypes.length > 0 ? weekTypes : null;
    const { error } = await supabase
      .from("programs")
      .update({ active_week_types: payload } as any)
      .eq("id", id);
    if (error) {
      const msg = error.message?.includes("déjà actif")
        ? "Another program is already active for this week type"
        : "Update error";
      toastError(msg, "Update program week types", error.message);
    } else {
      toast.success(payload ? "Week types updated" : "Program deactivated from weeks");
      invalidatePrograms();
      localStorage.setItem("perform_program_updated_at", new Date().toISOString());
    }
  };

  const deleteProgram = async (id: string) => {
    await supabase.from("program_sessions").delete().eq("program_id", id);
    const { error } = await supabase.from("programs").delete().eq("id", id);
    if (error) toastError("Deletion error", "Delete program", error.message);
    else {
      toast.success("Program deleted");
      if (currentProgramId === id) {
        if (user) {
          await supabase
            .from("user_intent_profiles")
            .update({ current_program_id: null } as any)
            .eq("user_id", user.id);
        }
        queryClient.invalidateQueries({ queryKey: programKeys.currentId(user?.id ?? null) });
        queryClient.invalidateQueries({ queryKey: ["current-program"] });
        emitProfileChange();
      }
      invalidatePrograms();
      localStorage.setItem("perform_program_updated_at", new Date().toISOString());
    }
  };

  const createEmptyProgram = async (title: string, durationWeeks = 4): Promise<ProgramRecord | null> => {
    if (!user) return null;
    const { data: { session: authSession } } = await supabase.auth.getSession();
    if (!authSession) { toastError("Session expired", "Create empty program"); return null; }

    const { data, error } = await supabase
      .from("programs")
      .insert({
        user_id: user.id,
        title,
        mode: "manual",
        detail_level: "simple",
        duration_weeks: durationWeeks,
        status: "saved",
        source_fact_ids: [],
        content_json: {},
        meta: {},
      } as any)
      .select()
      .single();
    if (error) { toastError("Creation error", "Create empty program", error.message); return null; }

    await supabase.from("notifications").insert({
      user_id: user.id,
      type: "program_saved",
      title: "📦 Programme enregistré",
      body: `"${title}" a été ajouté à ta bibliothèque.`,
      meta: { program_id: (data as any).id, program_title: title },
    } as any);
    queryClient.invalidateQueries({ queryKey: ["notifications"] });

    invalidatePrograms();
    localStorage.setItem("perform_program_updated_at", new Date().toISOString());
    return data as unknown as ProgramRecord;
  };

  const createProgramWithContentJson = async (
    title: string,
    durationWeeks: number,
    contentJson: any
  ): Promise<ProgramRecord | null> => {
    if (!user) return null;
    const { data: { session: authSession } } = await supabase.auth.getSession();
    if (!authSession) { toastError("Session expired", "Create a program"); return null; }

    const { data, error } = await supabase
      .from("programs")
      .insert({
        user_id: user.id,
        title,
        mode: "manual",
        detail_level: "simple",
        duration_weeks: durationWeeks,
        status: "saved",
        source_fact_ids: [],
        content_json: contentJson,
        meta: {
          summary: "Programme manuel (V2)",
          warnings: [],
          safety_adjustments: [],
          metadata: {
            detail_level: "simple",
            duration_weeks: durationWeeks,
            mode: "manual",
            generated_at: new Date().toISOString(),
          },
        },
      } as any)
      .select()
      .single();
    if (error) { toastError("Creation error", "Create a program", error.message); return null; }

    await supabase.from("notifications").insert({
      user_id: user.id,
      type: "program_saved",
      title: "📦 Programme enregistré",
      body: `"${title}" a été ajouté à ta bibliothèque.`,
      meta: { program_id: (data as any).id, program_title: title },
    } as any);
    queryClient.invalidateQueries({ queryKey: ["notifications"] });

    invalidatePrograms();
    localStorage.setItem("perform_program_updated_at", new Date().toISOString());
    return data as unknown as ProgramRecord;
  };

  const linkSessionToProgram = async (sessionId: string, programId: string, sortOrder?: number, note?: string, scheduledTime?: string | null): Promise<boolean> => {
    const { data: existing } = await supabase
      .from("program_sessions")
      .select("id")
      .eq("program_id", programId)
      .eq("session_id", sessionId)
      .maybeSingle();
    if (existing) { toast.info("Session already in this program"); return false; }

    if (sortOrder === undefined) {
      const { data: maxData } = await supabase
        .from("program_sessions")
        .select("sort_order")
        .eq("program_id", programId)
        .order("sort_order", { ascending: false })
        .limit(1)
        .maybeSingle();
      const nextOrder = maxData ? (maxData as any).sort_order + 1 : 0;
      const { error } = await supabase.from("program_sessions").insert({
        program_id: programId,
        session_id: sessionId,
        sort_order: nextOrder,
        ...(note ? { note } : {}),
        ...(scheduledTime ? { scheduled_time: scheduledTime } : {}),
      } as any);
      if (error) { toastError("Add error", "Link session to program", error.message); return false; }
    } else {
      const { error } = await supabase.from("program_sessions").insert({
        program_id: programId,
        session_id: sessionId,
        sort_order: sortOrder,
        ...(note ? { note } : {}),
        ...(scheduledTime ? { scheduled_time: scheduledTime } : {}),
      } as any);
      if (error) { toastError("Add error", "Link session to program", error.message); return false; }
    }
    invalidatePrograms();
    localStorage.setItem("perform_program_updated_at", new Date().toISOString());
    return true;
  };

  const updateProgramSessionTime = async (programSessionId: string, scheduledTime: string | null): Promise<boolean> => {
    const { error } = await supabase
      .from("program_sessions")
      .update({ scheduled_time: scheduledTime } as any)
      .eq("id", programSessionId);
    if (error) { toastError("Update error", "Update session time", error.message); return false; }
    return true;
  };

  return {
    programs,
    loading,
    currentProgramId,
    programsCount,
    fetchPrograms,
    fetchProgramsPage,
    saveProgram,
    updateProgramStatus,
    updateProgramContent,
    duplicateProgram,
    setAsCurrentProgram,
    setActiveWeekTypes,
    toggleProgramFavorite,
    deleteProgram,
    createEmptyProgram,
    createProgramWithContentJson,
    linkSessionToProgram,
    updateProgramSessionTime,
    activateProgramVersion,
    checkLineageDraftExists,
  };
}
